<template>
  <div class="pin-input-component">
    <div class="flex gap-4 mb-7">
      <input 
        v-for="(digit, index) in pinCode" 
        :key="index"
        type="text" 
        class="form-control-code" 
        :id="`${inputId}-${index}`"
        v-model="pinCode[index]"
        maxlength="1"
        @input="handleInput(index, $event)"
        @keydown="handleKeydown(index, $event)"
        @paste="handlePaste"
        placeholder=""
        inputmode="numeric"
        pattern="[0-9]"
      >
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive, watch, nextTick, PropType } from 'vue'

export default defineComponent({
  name: 'PinInput',
  props: {
    inputId: {
      type: String,
      default: 'pin-code'
    },
    modelValue: {
      type: String,
      default: ''
    },
    autoFocus: {
      type: Boolean,
      default: true
    },
    length: {
      type: Number,
      default: 6
    }
  },
  emits: ['update:modelValue', 'complete'],
  setup(props, { emit }) {
    const pinCode = reactive<string[]>(Array(props.length).fill(''))

    // Focus sur le premier input au montage
    if (props.autoFocus) {
      nextTick(() => {
        focusInput(0)
      })
    }

    // Synchroniser avec modelValue
    watch(() => props.modelValue, (newValue) => {
      if (newValue && newValue.length === props.length) {
        newValue.split('').forEach((digit, index) => {
          if (index < props.length) {
            pinCode[index] = digit
          }
        })
      } else if (!newValue) {
        pinCode.forEach((_, index) => {
          pinCode[index] = ''
        })
      }
    }, { immediate: true })

    const focusInput = (index: number) => {
      nextTick(() => {
        const input = document.getElementById(`${props.inputId}-${index}`) as HTMLInputElement
        if (input) {
          input.focus()
        }
      })
    }

    const handleInput = (index: number, event: Event) => {
      const target = event.target as HTMLInputElement
      const value = target.value

      // N'autoriser que les chiffres
      if (!/^\d*$/.test(value)) {
        pinCode[index] = ''
        return
      }

      // Focus automatique sur le suivant
      if (value.length === 1 && index < props.length - 1) {
        focusInput(index + 1)
      }

      // Émettre la valeur complète
      const fullCode = pinCode.join('')
      emit('update:modelValue', fullCode)

      // Émettre l'événement complete si le code est complet
      const digitsRegex = new RegExp(`^\\d{${props.length}}$`)
      if (fullCode.length === props.length && digitsRegex.test(fullCode)) {
        emit('complete', fullCode)
      }
    }

    const handleKeydown = (index: number, event: KeyboardEvent) => {
      if (event.key === 'Backspace' && pinCode[index] === '' && index > 0) {
        event.preventDefault()
        focusInput(index - 1)
      } else if (event.key === 'ArrowLeft' && index > 0) {
        event.preventDefault()
        focusInput(index - 1)
      } else if (event.key === 'ArrowRight' && index < props.length - 1) {
        event.preventDefault()
        focusInput(index + 1)
      }
    }

    const handlePaste = (event: ClipboardEvent) => {
      event.preventDefault()
      const pastedData = event.clipboardData?.getData('text') || ''
      const digitsRegex = new RegExp(`^\\d{${props.length}}$`)
      
      if (digitsRegex.test(pastedData)) {
        pastedData.split('').forEach((digit, index) => {
          if (index < props.length) {
            pinCode[index] = digit
          }
        })
        focusInput(props.length - 1)
        const fullCode = pinCode.join('')
        emit('update:modelValue', fullCode)
        emit('complete', fullCode)
      }
    }

    return {
      pinCode,
      handleInput,
      handleKeydown,
      handlePaste
    }
  }
})
</script>

<style scoped>
.pin-input-component {
  width: 100%;
}
</style>

